import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/store/api';
import { AgentStatusTable } from '@/components/common/AgentStatusTable';
import { transformMembersToStatusData } from '@/utils/agentStatusTransform';

const {
  useGetMyRoutingProfileUsersQuery,
  useGetAgentCcpStatusForUsersQuery,
  useGetAgentQueueStatusQuery,
  useGetRoutingProfilesQuery,
} = api;

/**
 * Live agent status table for the current agent's queue members
 * Displays all agents in the agent's routing profile with real-time status, active calls, and queue info
 */
export function AgentQueueStatusLive() {
  const { t } = useTranslation();

  const {
    data: routingData,
    isFetching: isFetchingRoutingData,
    refetch: refetchRoutingData,
  } = useGetMyRoutingProfileUsersQuery();

  const connectIds = useMemo(() => {
    return routingData?.users
      ?.map(u => u.connectId)
      ?.filter(Boolean) as string[];
  }, [routingData]);

  const {
    data: agentCcpStatusData,
    isFetching: isFetchingCcpStatus,
    refetch: refetchAgentCcpStatus,
  } = useGetAgentCcpStatusForUsersQuery(
    { connectIds: connectIds || [] },
    {
      skip: !connectIds || connectIds.length === 0,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: allAgentsQueueData,
    isFetching: isFetchingQueueStatus,
    refetch: refetchQueueStatus,
  } = useGetAgentQueueStatusQuery(
    { agentIds: connectIds?.join(',') || '' },
    {
      skip: !connectIds || connectIds.length === 0,
      refetchOnMountOrArgChange: true,
      pollingInterval: 30_000,
    }
  );

  const {
    data: routingProfilesData,
    isFetching: isFetchingRoutingProfilesList,
    refetch: refetchRoutingProfiles,
  } = useGetRoutingProfilesQuery();

  const isRefreshing =
    isFetchingCcpStatus ||
    isFetchingQueueStatus ||
    isFetchingRoutingProfilesList;

  const isLoading =
    (isRefreshing || isFetchingRoutingData) &&
    (!routingData?.users || routingData.users.length === 0);

  const handleRefresh = async () => {
    await Promise.all([
      refetchRoutingData(),
      refetchAgentCcpStatus(),
      refetchQueueStatus(),
      refetchRoutingProfiles(),
    ]);
  };

  const members = useMemo(
    () =>
      routingData?.users?.map(u => ({
        connectId: u.connectId,
        userEmail: u.userEmail,
        routingProfileId: routingData?.routingProfileId,
        routingProfileName: routingData?.routingProfileName,
      })) || [],
    [routingData]
  );

  const tableMembers = useMemo(
    () =>
      transformMembersToStatusData(
        members,
        agentCcpStatusData,
        allAgentsQueueData,
        routingProfilesData,
        routingData?.routingProfileName,
        t
      ),
    [
      members,
      agentCcpStatusData,
      allAgentsQueueData,
      routingProfilesData,
      routingData?.routingProfileName,
      t,
    ]
  );

  const totalActiveCalls = useMemo(
    () => tableMembers.reduce((sum, member) => sum + member.activeCalls, 0),
    [tableMembers]
  );

  return (
    <AgentStatusTable
      members={tableMembers}
      isLoading={isLoading}
      totalActiveCalls={totalActiveCalls}
      onRefresh={handleRefresh}
      isRefreshing={isRefreshing}
    />
  );
}
